import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { ideasAPI, modulesAPI, restaurantsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { IdeaDetailModal } from '../components/IdeaDetailModal';
import {
  Search, 
  Plus, 
  Lightbulb, 
  Sparkles, 
  CheckCircle, 
  AlertCircle,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_COLORS = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-gray-100 text-gray-700',
};

const STATUS_COLORS = {
  submitted: 'bg-gray-100 text-gray-700',
  analyzed: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  in_development: 'bg-purple-100 text-purple-700',
};

const RECOMMENDATION_ICONS = {
  Accept: { icon: CheckCircle, color: 'text-green-600' },
  Review: { icon: AlertCircle, color: 'text-yellow-600' },
  Decline: { icon: XCircle, color: 'text-red-600' },
};

function scoreColorClass(score) {
  if (score >= 70) return 'text-green-600';
  if (score >= 50) return 'text-yellow-600';
  return 'text-red-600';
}

function AiScoreCell({ idea }) {
  if (idea.ai_status === 'analyzing' || idea.ai_status === 'pending') {
    return (
      <div className="flex items-center justify-center gap-1">
        <div className="w-4 h-4 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin"></div>
        <span className="text-xs text-[#999999]">Analyzing...</span>
      </div>
    );
  }
  if (idea.ai_score) {
    return <span className={`font-bold text-lg ${scoreColorClass(idea.ai_score)}`}>{idea.ai_score}</span>;
  }
  if (idea.ai_status === 'failed') {
    return <span className="text-xs text-red-500">Failed</span>;
  }
  return <span className="text-[#999999]">-</span>;
}

export default function IdeasPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [modules, setModules] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRestaurant, setFilterRestaurant] = useState('');
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [ideasRes, modulesRes, restaurantsRes] = await Promise.all([
        ideasAPI.getAll(),
        modulesAPI.getAll(),
        user?.role === 'admin' ? restaurantsAPI.getAll() : Promise.resolve({ data: [] }),
      ]);
      setIdeas(ideasRes.data);
      setModules(modulesRes.data);
      setRestaurants(restaurantsRes.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Fast poll when AI analysis is pending
  useEffect(() => {
    const hasPending = ideas.some(i => i.ai_status === 'pending' || i.ai_status === 'analyzing');
    if (!hasPending) return;
    const fastPoll = setInterval(fetchData, 3000);
    return () => clearInterval(fastPoll);
  }, [ideas, fetchData]);

  const handleAnalyze = async (ideaId) => {
    setAnalyzing(true);
    try {
      const res = await ideasAPI.analyze(ideaId);
      toast.success('AI analysis complete');
      fetchData();
      if (selectedIdea?.id === ideaId) {
        const updatedIdea = await ideasAPI.getOne(ideaId);
        setSelectedIdea(updatedIdea.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCreateTask = async (ideaId, moduleId) => {
    setCreatingTask(true);
    try {
      await ideasAPI.toTask(ideaId, moduleId);
      toast.success('Task created from idea');
      fetchData();
      setSelectedIdea(null);
    } catch (err) {
      toast.error('Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  };

  const handleUpdateStatus = async (ideaId, status) => {
    try {
      await ideasAPI.update(ideaId, { status });
      toast.success('Status updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const filteredIdeas = ideas.filter(idea => {
    if (searchQuery && !idea.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterCategory && idea.category !== filterCategory) return false;
    if (filterStatus && idea.status !== filterStatus) return false;
    if (filterRestaurant && idea.restaurant_id !== filterRestaurant) return false;
    return true;
  });

  const categories = [...new Set(ideas.map(i => i.category))].filter(Boolean);

  if (loading) {
    return (
      <DashboardLayout pageTitle="Idea Bank">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#CFFF40] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Idea Bank">
      <div className="space-y-6" data-testid="ideas-page">
        {/* Top Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={16} />
              <Input
                placeholder="Search ideas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full sm:w-64"
                data-testid="search-ideas"
              />
            </div>
            
            <div className="flex gap-2 overflow-x-auto pb-1">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm bg-white min-w-0 flex-shrink-0"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm bg-white min-w-0 flex-shrink-0"
              >
                <option value="">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="analyzed">Analyzed</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
              
              {user?.role === 'admin' && restaurants.length > 0 && (
                <select
                  value={filterRestaurant}
                  onChange={(e) => setFilterRestaurant(e.target.value)}
                  className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm bg-white min-w-0 flex-shrink-0"
                  data-testid="filter-restaurant"
                >
                  <option value="">All Restaurants</option>
                  {restaurants.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
          
          {(user?.role === 'restaurant' || user?.role === 'partner' || user?.role === 'admin') && (
            <Button
              onClick={() => navigate('/submit-idea')}
              className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white flex-shrink-0"
              data-testid="submit-idea-btn"
            >
              <Plus size={16} className="mr-2" />
              Submit Idea
            </Button>
          )}
        </div>

        {/* Ideas Table - Desktop */}
        <div className="hidden md:block bg-white border border-[#E0E0E0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F5F5F5]">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-[#666666]">Title</th>
                {user?.role !== 'restaurant' && (
                  <th className="text-left p-4 text-sm font-medium text-[#666666]">Restaurant</th>
                )}
                <th className="text-left p-4 text-sm font-medium text-[#666666]">Submitted By</th>
                <th className="text-left p-4 text-sm font-medium text-[#666666]">Category</th>
                <th className="text-left p-4 text-sm font-medium text-[#666666]">Priority</th>
                {user?.role !== 'restaurant' && (
                  <th className="text-center p-4 text-sm font-medium text-[#666666]">AI Score</th>
                )}
                {user?.role !== 'restaurant' && (
                  <th className="text-center p-4 text-sm font-medium text-[#666666]">Recommendation</th>
                )}
                <th className="text-left p-4 text-sm font-medium text-[#666666]">Status</th>
                <th className="p-4 text-sm font-medium text-[#666666]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredIdeas.map(idea => {
                const RecIcon = idea.ai_analysis?.recommendation 
                  ? RECOMMENDATION_ICONS[idea.ai_analysis.recommendation]?.icon 
                  : null;
                const recColor = idea.ai_analysis?.recommendation 
                  ? RECOMMENDATION_ICONS[idea.ai_analysis.recommendation]?.color 
                  : '';
                
                return (
                  <tr
                    key={idea.id}
                    className="border-t border-[#E0E0E0] hover:bg-[#F5F5F5]"
                    data-testid={`idea-row-${idea.id}`}
                  >
                    <td className="p-4">
                      <button
                        onClick={() => setSelectedIdea(idea)}
                        className="font-medium text-[#111111] hover:text-[#FF6B35] text-left"
                      >
                        {idea.title}
                      </button>
                    </td>
                    {user?.role !== 'restaurant' && (
                      <td className="p-4 text-sm text-[#666666]">{idea.restaurant_name || '-'}</td>
                    )}
                    <td className="p-4 text-sm text-[#666666]">{idea.submitted_by_name || '-'}</td>
                    <td className="p-4 text-sm text-[#666666]">{idea.category}</td>
                    <td className="p-4">
                      <Badge className={PRIORITY_COLORS[idea.priority] || PRIORITY_COLORS.medium}>
                        {idea.priority}
                      </Badge>
                    </td>
                    {user?.role !== 'restaurant' && (
                    <td className="p-4 text-center">
                      <AiScoreCell idea={idea} />
                    </td>
                    )}
                    {user?.role !== 'restaurant' && (
                    <td className="p-4 text-center">
                      {RecIcon && (
                        <div className={`flex items-center justify-center gap-1 ${recColor}`}>
                          <RecIcon size={16} />
                          <span className="text-sm font-medium">{idea.ai_analysis?.recommendation}</span>
                        </div>
                      )}
                    </td>
                    )}
                    <td className="p-4">
                      <Badge className={STATUS_COLORS[idea.status] || STATUS_COLORS.submitted}>
                        {idea.status}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedIdea(idea)}
                        >
                          View
                        </Button>
                        {(user?.role === 'admin' || user?.role === 'partner') && !idea.ai_analysis && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAnalyze(idea.id)}
                            disabled={analyzing}
                            data-testid={`analyze-btn-${idea.id}`}
                          >
                            <Sparkles size={14} className="mr-1" />
                            Analyze
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          
          {filteredIdeas.length === 0 && (
            <div className="p-8 text-center">
              <Lightbulb size={48} className="mx-auto text-[#E0E0E0] mb-4" />
              <p className="text-[#666666]">No ideas found</p>
            </div>
          )}
        </div>

        {/* Ideas Cards - Mobile */}
        <div className="md:hidden space-y-3">
          {filteredIdeas.map(idea => {
            const RecIcon = idea.ai_analysis?.recommendation
              ? RECOMMENDATION_ICONS[idea.ai_analysis.recommendation]?.icon
              : null;
            const recColor = idea.ai_analysis?.recommendation
              ? RECOMMENDATION_ICONS[idea.ai_analysis.recommendation]?.color
              : '';
            return (
              <div
                key={idea.id}
                className="bg-white border border-[#E0E0E0] rounded-lg p-4 space-y-3"
                onClick={() => setSelectedIdea(idea)}
                data-testid={`idea-card-${idea.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-medium text-sm text-[#111111] flex-1">{idea.title}</h3>
                  {user?.role !== 'restaurant' && idea.ai_score && (
                    <span className={`font-bold text-lg flex-shrink-0 ${scoreColorClass(idea.ai_score)}`}>{idea.ai_score}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Badge className={PRIORITY_COLORS[idea.priority] || PRIORITY_COLORS.medium} style={{fontSize: '11px'}}>{idea.priority}</Badge>
                  <Badge className={STATUS_COLORS[idea.status] || STATUS_COLORS.submitted} style={{fontSize: '11px'}}>{idea.status}</Badge>
                  {idea.category && <Badge variant="outline" style={{fontSize: '11px'}}>{idea.category}</Badge>}
                  {user?.role !== 'restaurant' && RecIcon && (
                    <div className={`flex items-center gap-0.5 ${recColor}`}>
                      <RecIcon size={14} />
                      <span className="text-xs font-medium">{idea.ai_analysis?.recommendation}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-[#666666]">
                  <span>{idea.submitted_by_name || '-'}</span>
                  {idea.restaurant_name && <span>{idea.restaurant_name}</span>}
                </div>
              </div>
            );
          })}
          {filteredIdeas.length === 0 && (
            <div className="p-8 text-center bg-white border border-[#E0E0E0] rounded-lg">
              <Lightbulb size={48} className="mx-auto text-[#E0E0E0] mb-4" />
              <p className="text-[#666666]">No ideas found</p>
            </div>
          )}
        </div>
      </div>

      {/* Idea Detail Modal */}
      <IdeaDetailModal
        idea={selectedIdea}
        user={user}
        modules={modules}
        analyzing={analyzing}
        onClose={() => setSelectedIdea(null)}
        onAnalyze={handleAnalyze}
        onCreateTask={handleCreateTask}
        onUpdateStatus={handleUpdateStatus}
      />
    </DashboardLayout>
  );
}
